import { useEffect, useRef, useState } from "react";

import { TOAST_EVENT, type ToastDetail, type ToastVariant } from "@/lib/toast";

/**
 * 앱 전역 토스트 수신·렌더 (스펙 F §5 — .toast-wrap/.toast).
 *
 * fireToast(@/lib/toast)가 발화한 TOAST_EVENT를 수신해 우하단에 일정 시간 표시함.
 * 기존엔 AppShell 안에만 있어 로그인 등 AppShell 밖 공개 화면에선 토스트가 뜨지 않았음.
 * 이를 공용 컴포넌트로 분리해 AppShell + 로그인 양쪽에 마운트한다(DRY, 단일 렌더 계약).
 */
interface Toast {
  id: number;
  title: string;
  sub?: string;
  variant: ToastVariant;
}

/** 종류별 아이콘 — ok=체크(초록), warn=느낌표(노랑). 배경색은 CSS(.toast .ic.ok/.warn). */
function ToastIcon({ variant }: { variant: ToastVariant }) {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor" as const,
    strokeWidth: 2.5,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    width: 14,
    height: 14,
    "aria-hidden": true,
  };
  return (
    <span className={`ic ${variant}`}>
      {variant === "warn" ? (
        <svg {...common}>
          <line x1="12" y1="7.5" x2="12" y2="13" />
          <line x1="12" y1="16.5" x2="12" y2="16.5" />
        </svg>
      ) : (
        <svg {...common}>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </span>
  );
}

export function ToastHost() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdRef = useRef(0);

  useEffect(() => {
    const handler = (e: Event) => {
      const { title, sub, variant } = (e as CustomEvent<ToastDetail>).detail;
      const id = ++toastIdRef.current;
      setToasts((prev) => [...prev, { id, title, sub, variant: variant ?? "ok" }]);
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3600);
    };
    window.addEventListener(TOAST_EVENT, handler);
    return () => window.removeEventListener(TOAST_EVENT, handler);
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="toast-wrap">
      {toasts.map((t) => (
        <div key={t.id} className="toast">
          <ToastIcon variant={t.variant} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{t.title}</div>
            {t.sub && <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>{t.sub}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}
